import type {
  DailyBaselines,
  DailyScores,
  DetectedTrend,
  HealthDataSource,
  NormalizedDailyMetrics,
} from '@vitalai/health-models';
import type { DailyInsightResult, FoodDetectionResult } from '@vitalai/ai-core';
import type { MealLogInput } from '@vitalai/nutrition-core';

const DEFAULT_BASE_URL = 'http://localhost:8787';
const USER_EMAIL_STORAGE_KEY = 'vitalai.user-email';
const DEVICE_ID_STORAGE_KEY = 'vitalai.device-id';

export function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? DEFAULT_BASE_URL;
}

/**
 * Sprint 3 no tiene autenticacion real: el Worker identifica al usuario por
 * el header X-User-Email. Generamos un email estable por navegador/dispositivo
 * y lo reusamos entre sesiones para que el mismo usuario demo persista en D1.
 */
function getOrCreateStoredValue(key: string, generate: () => string): string {
  try {
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;
    const value = generate();
    window.localStorage.setItem(key, value);
    return value;
  } catch {
    return generate();
  }
}

export function getDemoUserEmail(): string {
  return getOrCreateStoredValue(USER_EMAIL_STORAGE_KEY, () => `demo-${crypto.randomUUID().slice(0, 8)}@vitalai.local`);
}

export function getDeviceId(): string {
  return getOrCreateStoredValue(DEVICE_ID_STORAGE_KEY, () => crypto.randomUUID());
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-User-Email': getDemoUserEmail(),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Error ${response.status} al llamar a ${path}`);
  }

  return (await response.json()) as T;
}

export async function checkApiStatus(signal?: AbortSignal): Promise<boolean> {
  try {
    const response = await fetch(`${getApiBaseUrl()}/api/status`, { signal });
    return response.ok;
  } catch {
    return false;
  }
}

export interface TodayResponse {
  date: string;
  metrics: NormalizedDailyMetrics | null;
  scores: DailyScores | null;
  baselines: DailyBaselines;
  trends: DetectedTrend[];
  lastSyncAt: string | null;
}

export async function fetchToday(signal?: AbortSignal): Promise<TodayResponse | null> {
  try {
    return await apiFetch<TodayResponse>('/api/health/today', { signal });
  } catch {
    // 404 esperado cuando el usuario todavia no sincronizo ningun dato.
    return null;
  }
}

export async function fetchHistory(days = 90, signal?: AbortSignal): Promise<NormalizedDailyMetrics[]> {
  const result = await apiFetch<{ days: NormalizedDailyMetrics[] }>(`/api/health/history?days=${days}`, { signal });
  return result.days;
}

export interface SyncResult {
  recordsProcessed: number;
  syncedDates: string[];
}

export async function syncDays(days: NormalizedDailyMetrics[], source: HealthDataSource, signal?: AbortSignal): Promise<SyncResult> {
  return apiFetch<SyncResult>('/api/health/sync', {
    method: 'POST',
    body: JSON.stringify({ source, deviceId: getDeviceId(), days }),
    signal,
  });
}

export interface LogMealResult {
  date: string;
  scores: DailyScores;
}

export async function logMeal(input: MealLogInput, signal?: AbortSignal): Promise<LogMealResult> {
  return apiFetch<LogMealResult>('/api/nutrition/meals', {
    method: 'POST',
    body: JSON.stringify(input),
    signal,
  });
}

/** Sube una foto de comida a R2 (via el Worker). */
export async function uploadFoodPhoto(file: Blob, signal?: AbortSignal): Promise<{ photoKey: string }> {
  return apiFetch<{ photoKey: string }>('/api/nutrition/photos', {
    method: 'POST',
    headers: { 'Content-Type': file.type || 'image/jpeg' },
    body: file,
    signal,
  });
}

/**
 * Analiza una foto ya subida (por su photoKey) con un modelo de vision.
 * Devuelve estimaciones: la UI SIEMPRE debe mostrarlas en una pantalla
 * editable antes de guardar, nunca guardarlas directo.
 */
export async function analyzeFoodPhoto(photoKey: string, signal?: AbortSignal): Promise<FoodDetectionResult> {
  return apiFetch<FoodDetectionResult>('/api/nutrition/analyze-image', {
    method: 'POST',
    body: JSON.stringify({ photoKey }),
    signal,
  });
}

export interface DailyInsightResponse {
  date: string;
  insight: DailyInsightResult;
  cached: boolean;
}

export async function fetchDailyInsight(force = false, signal?: AbortSignal): Promise<DailyInsightResponse> {
  return apiFetch<DailyInsightResponse>(`/api/ai/daily-insight${force ? '?force=true' : ''}`, {
    method: 'POST',
    signal,
  });
}
